import React, { useState, useEffect } from 'react';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { Calendar } from '../../../../../shared/components/widgets/calendar/calendar.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import type { StayDurationItem } from '../../../../../shared/models/unit-types.model';

export interface AddStayDurationModalProps {
  data: {
    stayDuration: StayDurationItem[];
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    denyAction: () => void;
    confirmAction: () => void;
    addStayDuration: (durations: StayDurationItem[]) => void;
  };
}

export const AddStayDurationModal: React.FC<AddStayDurationModalProps> = ({
  eventHandlers,
  config,
  data
}) => {
  const isMobileView = useWindowResizer();
  const [selectedDates, setSelectedDates] = useState<[Date, Date][]>();
  const [disabledDates, setDisabledDates] = useState<Date[]>();
  const [minimumStay, setMinimumStay] = useState<string>('');
  const [showOverLappingError, setShowOverLappingError] = useState<boolean>(false);

  useEffect(() => {
    if (config.showModal) {
      setMinimumStay('');
      setSelectedDates(undefined);
      setShowOverLappingError(false);
    }
  }, [config.showModal]);

  useEffect(() => {
    const tempDisabledDated =
      data.stayDuration && data.stayDuration.length > 0
        ? data.stayDuration.reduce((acc: Date[], availObj: StayDurationItem) => {
            const dates = getDaysArray(new Date(availObj.from), new Date(availObj.to));
            return [...acc, ...dates];
          }, [])
        : [];
    setDisabledDates(tempDisabledDated);
  }, [data.stayDuration]);

  const convertDatesToDurations = (dateRanges: any[], minStay: string): StayDurationItem[] => {
    const durations = [];
    for (let i = 0; i < dateRanges.length; i += 2) {
      if (i + 1 < dateRanges.length) {
        durations.push({
          from: new Date(dateRanges[i]).getTime(),
          to: new Date(dateRanges[i + 1]).getTime(),
          minimumStay: parseInt(minStay) || 1
        });
      }
    }
    return durations;
  };

  const handleAddDuration = (isOverLap: boolean) => {
    if (isOverLap) {
      setShowOverLappingError(true);
    } else {
      setShowOverLappingError(false);
      if (selectedDates && minimumStay) {
        const newDurations = convertDatesToDurations(selectedDates, minimumStay);
        eventHandlers.addStayDuration(newDurations);
      }
      eventHandlers.denyAction();
    }
  };

  const checkDateOverlap = (date: any) => {
    let isDateOverlap = false;
    data?.stayDuration?.forEach((item) => {
      if (date[0] <= item.to && item.from <= date[1]) {
        isDateOverlap = true;
      } else isDateOverlap = false;
    });
    handleAddDuration(isDateOverlap);
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          setSelectedDates(undefined);
          eventHandlers.denyAction();
        }
      }}
    >
      <div className="p-6">
        <div className="text-xl font-bold text-gray-800 mb-4">Add Min Stay Duration</div>
        <div className="mb-4">
          <div className="mb-2">
            <span className="text-gray-700">Minimum Stay (days):</span>
            <InputText
              data={{
                value: minimumStay
              }}
              config={{
                maxRows: 1,
                autoFocus: true,
                type: InputTextType.number
              }}
              eventHandlers={{
                onChangeHandler: (value: string) => setMinimumStay(value)
              }}
            />
          </div>
          {showOverLappingError && (
            <div className="text-red-500 text-sm mt-1">Can't pick this date, it's overlapping</div>
          )}
          <Calendar
            data={{
              calenderView: true,
              disbaleDate: disabledDates,
              basePriceTag: 0,
              showDateInput: false,
              multipleRanges: true,
              dateRanges: selectedDates
            }}
            eventHandlers={{
              onChange: (val) => setSelectedDates(val as unknown as [Date, Date][])
            }}
          />
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button
            onClick={eventHandlers.denyAction}
            type={ButtonType.primaryInverted}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            No
          </Button>
          <Button
            onClick={() => checkDateOverlap(selectedDates)}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            disabled={!selectedDates || minimumStay === ''}
          >
            Yes
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const getDaysArray = (s: any, e: any) => {
  const a: Date[] = [];
  // `to` is already inclusive (end of the last day), so don't extend it by a day.
  for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    a.push(new Date(d));
  }
  return a;
};
